import { useState, useEffect, useRef } from "react";
import {
  ArrowRight,
  Check,
  Phone,
  FileSearch,
  ClipboardList,
  Users,
  ShieldCheck,
  Clock,
  TrendingUp,
  Settings,
  ChevronRight,
  CheckCircle2,
  Minus,
  Plus,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/e7qtpwyy_Bazaart_2CA00B66-0594-4702-AC94-D9D6D5BB8442%20%281%29.JPEG";
const IMG_HERO = "https://customer-assets.emergentagent.com/job_7865eaef-d264-4504-a997-7bd20f6ab0e8/artifacts/s6kowdyp_IMG_6735.jpeg";

const useInView = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const REVIEW_CALL_ITEMS = [
  "Discuss the notice, report, or concern that prompted your outreach",
  "Review any information you currently have available",
  "Answer questions about enrollment, credentialing, or recredentialing requirements",
  "Identify any obvious risks or areas that may require additional review",
  "Determine whether a more comprehensive assessment would provide value",
];

const FAQS = [
  { q: "Is the review call really free?", a: "Yes. The complimentary review call is designed to help you understand your situation with no obligation. If a more comprehensive assessment is recommended, we will explain the scope and pricing clearly before anything moves forward." },
  { q: "What if our credentialing is already in good shape?", a: "That happens. Sometimes the review confirms everything is on track and no further action is needed. Either way, you will have clarity and confidence about where things stand." },
  { q: "How long is the call?", a: "Most review calls take 15 to 20 minutes. Enough time to understand your situation and determine whether further review would be valuable." },
  { q: "What should I have ready for the call?", a: "If you have the notice or report that prompted your concern, that helps. But it is not required. We can walk through your situation together on the call." },
];

const CredentialingQuickReview = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="bg-white text-[#0B1F3A] font-body overflow-x-hidden">
      {/* HEADER */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#00838A]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <a href="/credentialing-quick-review" className="flex items-center"><img src={LOGO_URL} alt="RNB Consulting Group" className="h-12 w-auto rounded-lg" /></a>
          <a href="#book"><button className="bg-[#00838A] text-[#0B1F3A] font-bold text-sm px-5 py-2.5 rounded-full hover:scale-105 transition-transform">Schedule Free Review</button></a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A] via-[#0B1F3A] to-[#00838A]" />
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-8 pt-20 pb-0 md:pt-24">
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="pb-16 md:pb-24">
              <FadeIn>
                <span className="inline-block bg-[#00838A]/15 text-[#00838A] text-xs font-accent font-semibold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-6">Complimentary Review Call</span>
              </FadeIn>
              <FadeIn delay={80}>
                <h1 className="font-heading text-4xl sm:text-5xl md:text-[3.4rem] font-black tracking-tighter leading-[1.08] text-white mb-5">
                  Received a Medicaid Enrollment Notice or Revalidation Alert?
                </h1>
              </FadeIn>
              <FadeIn delay={120}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-[#00838A] mb-6">
                  Let{'\u2019'}s Make Sure Nothing Falls Through the Cracks
                </h2>
              </FadeIn>
              <FadeIn delay={160}>
                <p className="text-white/70 text-base leading-relaxed mb-8 max-w-lg">
                  Schedule a complimentary review call to help you understand what you{'\u2019'}re looking at and whether any action may be needed. No obligation, no pressure.
                </p>
              </FadeIn>
              <FadeIn delay={200}>
                <a href="#book">
                  <button className="bg-[#00838A] text-[#0B1F3A] font-bold text-sm px-7 py-3.5 rounded-full hover:scale-105 transition-transform shadow-lg shadow-[#00838A]/25 inline-flex items-center gap-2">
                    Schedule My Free Review Call <ArrowRight className="w-4 h-4" />
                  </button>
                </a>
              </FadeIn>
            </div>
            <FadeIn delay={150}>
              <div className="relative">
                <div className="rounded-t-2xl md:rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                  <img src={IMG_HERO} alt="Healthcare professionals reviewing enrollment records" className="w-full h-auto object-cover" />
                </div>
                <div className="absolute -bottom-5 left-6 bg-white rounded-xl shadow-xl px-5 py-3.5 flex items-center gap-3 border border-[#00838A]/10">
                  <div className="w-10 h-10 rounded-full bg-[#00838A]/15 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-[#00838A]" /></div>
                  <div>
                    <p className="font-heading font-bold text-sm text-[#0B1F3A]">100% Free</p>
                    <p className="text-xs text-[#5A5A5A]">15-20 minute call</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
        <div className="relative -mb-1"><svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full"><path d="M0 80V30C240 60 480 0 720 30C960 60 1200 0 1440 30V80H0Z" fill="white"/></svg></div>
      </section>

      {/* WHY THIS MATTERS */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <FadeIn>
            <div className="space-y-4 text-[#5A5A5A] text-base leading-relaxed">
              <p>If your practice recently received a Medicaid enrollment notice, appeared on a revalidation report, or has concerns about provider enrollment status, it may be worth taking a closer look.</p>
              <p>In many cases, there is nothing urgent to address.</p>
              <p>In other cases, missed follow-up, incomplete enrollment activity, or credentialing gaps can eventually impact reimbursements, provider participation, and administrative workload.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHAT HAPPENS ON THE CALL */}
      <section className="py-20 md:py-28 bg-[#E8F4F5]/25">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <FadeIn>
            <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-3">The review call</p>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight mb-4">What Happens During the Call?</h2>
            <p className="text-[#5A5A5A] text-base leading-relaxed mb-10 max-w-2xl">
              The goal is simple: help you understand whether there is a potential issue and what your options may be.
            </p>
          </FadeIn>
          <div className="space-y-3 mb-10">
            {REVIEW_CALL_ITEMS.map((item, i) => (
              <FadeIn key={`rc-${i}`} delay={80 + i * 50}>
                <div className="flex items-start gap-3 bg-white p-4 rounded-xl border border-[#00838A]/8">
                  <div className="w-6 h-6 rounded-full bg-[#00838A]/15 flex items-center justify-center mt-0.5 shrink-0"><Check className="w-3.5 h-3.5 text-[#00838A]" strokeWidth={3} /></div>
                  <span className="text-[#0B1F3A] text-sm">{item}</span>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={400}>
            <div className="bg-white rounded-lg p-4 border-l-4 border-[#00838A]/30">
              <p className="text-[#5A5A5A] text-sm leading-relaxed">
                <strong className="text-[#0B1F3A]">This is not a full audit or assessment.</strong> It{'\u2019'}s an opportunity to have an experienced healthcare operations professional review your situation and point you in the right direction.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA - SCHEDULE */}
      <section id="book" className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-3">Get started</p>
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight mb-3">Schedule your free review call.</h2>
              <p className="text-[#5A5A5A] text-base max-w-lg mx-auto">Pick a time that works. We{'\u2019'}ll talk through your situation and determine if any action is needed.</p>
            </div>
          </FadeIn>
          <FadeIn delay={100}>
            <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-[#00838A]/10 bg-white">
              <iframe src="https://calendly.com/rnb-consulting-group/credentialing-quick-review?hide_event_type_details=1&primary_color=0b1f3a&hide_gdpr_banner=1" title="Schedule a Credentialing Review Call" className="w-full border-0" style={{ minHeight: "700px" }} />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* NEED MORE? */}
      <section className="py-16 md:py-20 bg-[#0B1F3A]">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <FadeIn>
            <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-3">Need a deeper review?</p>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-white mb-4">
              Sometimes a quick call confirms everything is fine.
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-8 max-w-xl mx-auto">
              Other times, organizations discover they need a more comprehensive review. Our Credentialing &amp; Enrollment Assessment provides the full picture.
            </p>
            <a href="/medicaid-credentialing-services">
              <button className="border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center gap-2">
                Learn About Our Full Assessment <ArrowRight className="w-4 h-4" />
              </button>
            </a>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <p className="text-sm font-accent font-semibold uppercase tracking-[0.2em] text-[#00838A] mb-3">FAQ</p>
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Common questions</h2>
            </div>
          </FadeIn>
          <div className="space-y-3">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <FadeIn key={`faq-${i}`} delay={i * 60}>
                  <div className={`border rounded-xl transition-all ${isOpen ? "border-[#00838A]/20 shadow-md bg-white" : "border-[#00838A]/8 bg-white hover:border-[#00838A]/15"}`}>
                    <button onClick={() => setOpenFaq(isOpen ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                      <span className="font-heading font-bold text-[#0B1F3A] text-sm pr-4">{f.q}</span>
                      {isOpen ? <Minus className="w-5 h-5 text-[#00838A] shrink-0" /> : <Plus className="w-5 h-5 text-[#00838A] shrink-0" />}
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"}`}>
                      <p className="px-5 pb-5 text-[#5A5A5A] text-sm leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0B1F3A] text-white/60 text-sm py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3"><img src={LOGO_URL} alt="RNB Consulting Group" className="h-8 w-auto rounded" /><p className="font-heading font-bold text-white tracking-tight">RNB Consulting Group</p></div>
          <p>&copy; {new Date().getFullYear()} RNB Consulting Group. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CredentialingQuickReview;
